// ─────────────────────────────────────────────────────────────────────────────
// ContactSalesPage serves two routes from one file: /contact, where an operator
// writes to a person, and /demo, where they ask for a walkthrough. The page
// reads the pathname and swaps the heading, the button and the search metadata,
// so the two routes read as two pages and never compete for the same canonical.
//
// The layout is deliberately two columns and nothing else: the form on the left,
// and on the right what happens once it is sent, how long a walkthrough runs,
// what to have ready, and a plain email address for anyone who would rather not
// use a form at all.
//
// There is no form endpoint yet. Submitting composes a prefilled message in the
// sender's own mail client, which is the behaviour this page has always had, so
// the confirmation says exactly that rather than claiming a request was filed.
//
// No customer is named anywhere on this page. Nothing is claimed here that the
// product does not do, and every image is a captioned grey <MediaSlot>.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    BTN_MD,
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Display,
    Heading,
    CardTitle,
    Lead,
    Body,
    Eyebrow,
    Pill,
    Card,
    MediaSlot,
    SpecList,
    Steps,
    FeatureRow,
    CTASection,
    MarketingShell,
    Seo,
    SITE_URL,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

const SALES_EMAIL = 'sales@wavelengtherp.com';

// Monochrome field styling, matching the pill inputs used elsewhere on the site.
const FIELD =
    'h-11 w-full rounded-full border border-[rgba(10,10,10,0.12)] bg-white px-5 text-[15px] text-[#0A0A0A] outline-none transition-colors placeholder:text-[#A1A1A1] focus:border-[rgba(10,10,10,0.35)]';
const AREA =
    'w-full resize-none rounded-[24px] border border-[rgba(10,10,10,0.12)] bg-white px-5 py-3.5 text-[15px] leading-[1.55] text-[#0A0A0A] outline-none transition-colors placeholder:text-[#A1A1A1] focus:border-[rgba(10,10,10,0.35)]';
const LABEL = 'mb-2 block text-[13px] font-medium text-[#6B6B6B]';

const LOCATION_BANDS = ['1', '2 to 5', '6 to 20', 'More than 20'];

// What happens to the message after it is sent. Three steps, no promises about
// automation that does not exist.
const NEXT_STEPS = [
    {
        title: 'A person reads it',
        body: 'It is not routed into a queue and scored. Someone who works on the product reads what you wrote and answers it, within one business day.',
    },
    {
        title: 'You get an answer in writing first',
        body: 'If your question has a plain answer, it comes back in the reply. You should not have to sit through a call to find out whether something is supported.',
    },
    {
        title: 'Then a walkthrough, if you want one',
        body: 'Forty minutes on a screen share at a time you pick. We close a day, import a statement and work the exceptions, and you can bring whoever closes the day with you.',
    },
];

const HAVE_READY = [
    'How many locations you run, and which register each one runs',
    'Who closes the day today, and what they write it on',
    'How the bank statement reaches you, online or as a file',
    'The one number that never ties out',
];

const WALKTHROUGH_SPECS = [
    { label: 'Reply time', value: 'One business day' },
    { label: 'Walkthrough length', value: '40 minutes' },
    { label: 'Format', value: 'Screen share, nothing to install' },
    { label: 'Who should join', value: 'Whoever closes the day' },
    { label: 'Cost', value: 'None, and no obligation' },
];

// ── The form ─────────────────────────────────────────────────────────────────
// There is no backend for this yet. The submit composes a prefilled message in
// the sender's mail client, which is what this page already did, and the
// confirmation state says so plainly instead of claiming a ticket was raised.
function ContactForm({ isDemo }) {
    const [form, setForm] = useState({
        name: '',
        email: '',
        company: '',
        locations: '',
        registers: '',
        problem: '',
    });
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    function handleSubmit(e) {
        e.preventDefault();
        setError('');
        if (!form.name.trim()) { setError('Please enter your name.'); return; }
        if (!/\S+@\S+\.\S+/.test(form.email)) { setError('Enter a valid work email.'); return; }
        if (!form.company.trim()) { setError('Tell us the name of your business.'); return; }

        const subject = encodeURIComponent(
            `${isDemo ? 'Walkthrough request' : 'Enquiry'}: ${form.company}`,
        );
        const body = encodeURIComponent(
            `Name: ${form.name}\n` +
            `Work email: ${form.email}\n` +
            `Company: ${form.company}\n` +
            `Locations: ${form.locations || 'Not given'}\n` +
            `Registers in use: ${form.registers || 'Not given'}\n\n` +
            `What is not tying out today:\n${form.problem || 'Not given'}\n`,
        );
        window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
        setSubmitted(true);
    }

    if (submitted) {
        return (
            <Card radius="xl" className="flex flex-col gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                    <Icon name="check" className="h-5 w-5" strokeWidth={2.2} />
                </span>
                <CardTitle>Your message is ready to send</CardTitle>
                <Body>
                    Your mail client should have opened with everything you typed already in it. Send that
                    message and a person replies within one business day. Nothing has reached us until you
                    hit send, so if no window opened, the same details can go straight to the address below.
                </Body>
                <div className="mt-1 flex flex-wrap items-center gap-3">
                    <a href={`mailto:${SALES_EMAIL}`} className={BTN_SECONDARY}>
                        {SALES_EMAIL}
                    </a>
                    <button
                        type="button"
                        onClick={() => setSubmitted(false)}
                        className="text-[13px] font-medium text-[#6B6B6B] transition-colors hover:text-[#0A0A0A]"
                    >
                        Back to the form
                    </button>
                </div>
            </Card>
        );
    }

    return (
        <Card radius="xl" className="!p-6 sm:!p-8">
            <div className="mb-6 flex flex-col gap-1.5">
                <CardTitle>Tell us about your stores</CardTitle>
                <Body>
                    Six fields. The last two are the ones that make the first reply useful.
                </Body>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className={LABEL} htmlFor="cs-name">Name</label>
                        <input id="cs-name" className={FIELD} value={form.name} onChange={set('name')} placeholder="First and last name" />
                    </div>
                    <div>
                        <label className={LABEL} htmlFor="cs-email">Work email</label>
                        <input id="cs-email" type="email" className={FIELD} value={form.email} onChange={set('email')} placeholder="you@company.com" />
                    </div>
                </div>

                <div>
                    <label className={LABEL} htmlFor="cs-company">Company</label>
                    <input id="cs-company" className={FIELD} value={form.company} onChange={set('company')} placeholder="Your business name" />
                </div>

                <div>
                    <span className={LABEL}>Number of locations</span>
                    <div className="flex flex-wrap gap-2">
                        {LOCATION_BANDS.map((b) => (
                            <Pill
                                key={b}
                                as="button"
                                type="button"
                                active={form.locations === b}
                                onClick={() => setForm((f) => ({ ...f, locations: f.locations === b ? '' : b }))}
                            >
                                {b}
                            </Pill>
                        ))}
                    </div>
                </div>

                <div>
                    <label className={LABEL} htmlFor="cs-registers">Which register each location runs</label>
                    <textarea
                        id="cs-registers"
                        rows={2}
                        className={AREA}
                        value={form.registers}
                        onChange={set('registers')}
                        placeholder="Two stores on the same till system, one on something older, plus a web store"
                    />
                </div>

                <div>
                    <label className={LABEL} htmlFor="cs-problem">What is not tying out today</label>
                    <textarea
                        id="cs-problem"
                        rows={4}
                        className={AREA}
                        value={form.problem}
                        onChange={set('problem')}
                        placeholder="Cash comes up short on weekends and the deposits never match the statement"
                    />
                </div>

                {error && (
                    <p className="rounded-[16px] border border-[rgba(10,10,10,0.10)] bg-[#F5F5F5] px-4 py-3 text-[13px] leading-[1.45] text-[#0A0A0A]">
                        {error}
                    </p>
                )}

                <button type="submit" className={`${BTN_MD} w-full`}>
                    {isDemo ? 'Request a walkthrough' : 'Send it'}
                    <Icon name="arrow" className="h-4 w-4" />
                </button>

                <p className="text-[12px] leading-[1.6] text-[#A1A1A1]">
                    We use what you send here to reply to you and nothing else. See the{' '}
                    <Link to="/privacy" className="underline underline-offset-2 hover:text-[#0A0A0A]">privacy notice</Link>{' '}
                    and the{' '}
                    <Link to="/terms" className="underline underline-offset-2 hover:text-[#0A0A0A]">terms</Link>.
                </p>
            </form>
        </Card>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ContactSalesPage() {
    const { pathname } = useLocation();
    const isDemo = pathname.startsWith('/demo');
    const path = isDemo ? '/demo' : '/contact';

    return (
        <MarketingShell>
            <Seo
                title={isDemo
                    ? 'Book a walkthrough of the daily close and reconciliation'
                    : 'Contact us about back office and reconciliation'}
                path={path}
                description={isDemo
                    ? 'Book a forty minute walkthrough of Wavelength. We close a trading day, match a bank statement against processor payouts and work the exceptions queue live.'
                    : 'Talk to the Wavelength team about back office and reconciliation across several locations. Tell us how many stores you run and what is not tying out today.'}
                keywords={isDemo
                    ? 'book a back office software demo, retail reconciliation walkthrough, daily close software demo, multi location retail software demo, cash control demo, bank reconciliation demo for retail'
                    : 'contact back office software, retail reconciliation enquiry, multi location retail back office contact, daily close software questions, cash accountability software contact'}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'ContactPage',
                            '@id': `${SITE_URL}${path}#page`,
                            url: `${SITE_URL}${path}`,
                            name: isDemo ? 'Book a walkthrough' : 'Talk to the team',
                            description: isDemo
                                ? 'Request a forty minute screen-share walkthrough of the daily close, cash controls and three-way reconciliation.'
                                : 'Write to the Wavelength team about back-office and reconciliation for multiple locations.',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/overview` },
                                {
                                    '@type': 'ListItem',
                                    position: 2,
                                    name: isDemo ? 'Book a walkthrough' : 'Contact',
                                    item: `${SITE_URL}${path}`,
                                },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero: one column of type, the work happens below ────────── */}
            <Container className="pb-12 pt-16 sm:pb-16 sm:pt-24">
                <Reveal>
                    <div className="flex max-w-[720px] flex-col gap-5">
                        <Eyebrow>{isDemo ? 'Book a walkthrough' : 'Talk to the team'}</Eyebrow>
                        <Display>
                            {isDemo ? 'See it close a day.' : 'Ask us anything about it.'}
                        </Display>
                        <Lead className="max-w-[560px]">
                            {isDemo
                                ? 'Forty minutes on a screen share, no slides. We close a trading day, import a bank statement, match it against processor payouts and work whatever is left in the exceptions queue.'
                                : 'Tell us how many locations you run, what each one rings sales on, and which number never ties out. A person reads it and answers, usually the same day.'}
                        </Lead>
                    </div>
                </Reveal>
            </Container>

            {/* ── Two columns: the form, and what happens after you send it ─ */}
            <Section tone="paper2" pad="md">
                <Container>
                    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1.02fr_0.98fr] lg:gap-10">
                        <Reveal>
                            <ContactForm isDemo={isDemo} />
                        </Reveal>

                        <Reveal delay={80}>
                            <div className="flex flex-col gap-6">
                                <div className="flex flex-col gap-3">
                                    <Heading>What happens next.</Heading>
                                    <Lead className="max-w-[460px]">
                                        Three steps, and you can stop after any of them.
                                    </Lead>
                                </div>

                                <Steps items={NEXT_STEPS} />

                                <Card radius="lg" className="flex flex-col gap-4">
                                    <CardTitle>What to have ready</CardTitle>
                                    <ul className="flex flex-col gap-3">
                                        {HAVE_READY.map((h) => (
                                            <li key={h} className="flex items-start gap-3 text-[14px] leading-[1.55] text-[#3D3D3D]">
                                                <span className="mt-[3px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                                                    <Icon name="check" className="h-3 w-3" strokeWidth={2.2} />
                                                </span>
                                                <span>{h}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Body>
                                        None of it is required. It only means the first reply can be specific
                                        instead of a request for more detail.
                                    </Body>
                                </Card>

                                <SpecList rows={WALKTHROUGH_SPECS} />

                                <Card radius="lg" className="flex flex-col gap-3">
                                    <CardTitle>Would rather not use a form</CardTitle>
                                    <Body>
                                        Write to us directly and the same person answers. Monday to Friday,
                                        with a reply inside one business day.
                                    </Body>
                                    <div className="mt-1">
                                        <a href={`mailto:${SALES_EMAIL}`} className={BTN_SECONDARY}>
                                            {SALES_EMAIL}
                                            <Icon name="arrow" className="h-4 w-4" />
                                        </a>
                                    </div>
                                </Card>
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── What the walkthrough actually covers ─────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="The walkthrough"
                            title="We open the product, not a deck."
                            body="It is the same forty minutes whichever route you arrived on. You watch a day get closed and reconciled, and you ask questions while it happens."
                            bullets={[
                                'A day closed end to end: blind count, deposit, written reason on a short drawer, sign-off, and the lock',
                                'A bank statement imported and matched against register takings and processor batches',
                                'The exceptions queue worked through, with the location, the source, the date and the amount',
                                'The audit log, so you can see what a manual change leaves behind',
                            ]}
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="Reconciliation open on a laptop during a walkthrough call"
                                    note="Register takings, processor batches and bank deposits side by side, one row unmatched and selected"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ─────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Before you write"
                title="You can read the pricing first."
                body="Three tiers and the extra-location rate are published, so the first conversation can be about your stores rather than about getting a quote out of us."
                primary={{ label: 'See pricing', to: '/pricing' }}
                secondary={{ label: 'Email us', href: `mailto:${SALES_EMAIL}` }}
            />
        </MarketingShell>
    );
}
